import { useState } from 'react';
import { Link, useNavigate } from 'react-router-dom';
import { motion } from 'framer-motion';
import { Heart, ExternalLink, Star } from 'lucide-react';
import { useAuth } from '../context/AuthContext';
import { addToWishlist, removeFromWishlist } from '../api';
import { trackAffiliateClick } from '../api/admin';
import { toast } from 'sonner';

const ProductCard = ({ product, isWishlisted = false, onWishlistChange, variant = 'default' }) => {
  const [wishlisted, setWishlisted] = useState(isWishlisted);
  const [loading, setLoading] = useState(false);
  const { user } = useAuth();
  const navigate = useNavigate();

  const handleWishlistToggle = async (e) => {
    e.preventDefault();
    e.stopPropagation();
    
    if (!user) {
      navigate('/login');
      return;
    }

    setLoading(true);
    try {
      if (wishlisted) {
        await removeFromWishlist(product.id);
        toast.success('Removed from wishlist');
      } else {
        await addToWishlist(product.id);
        toast.success('Added to wishlist');
      }
      setWishlisted(!wishlisted);
      onWishlistChange?.(!wishlisted);
    } catch (error) {
      toast.error(error.response?.data?.detail || 'Failed to update wishlist');
    } finally {
      setLoading(false);
    }
  };

  const discount = product.original_price > product.price 
    ? Math.round((1 - product.price / product.original_price) * 100) 
    : 0;

  // Different sizes based on variant
  const isHero = variant === 'hero';
  const isTall = variant === 'tall';

  return (
    <motion.div
      initial={{ opacity: 0, y: 20 }}
      animate={{ opacity: 1, y: 0 }}
      whileHover={{ y: -5 }}
      className={`card-product group ${isHero ? 'row-span-2' : ''} ${isTall ? 'row-span-2' : ''}`}
      data-testid={`product-card-${product.id}`}
    >
      <Link to={`/product/${product.id}`}>
        {/* Image Container */}
        <div className={`product-image-container bg-[#121212] ${isHero ? 'aspect-[4/5]' : 'aspect-square'}`}>
          <img
            src={product.image_url}
            alt={product.name}
            className="w-full h-full object-cover"
            loading="lazy"
          />
          
          {/* Tags */}
          <div className="absolute top-3 left-3 flex gap-2">
            {product.is_hot && <span className="tag-hot">HOT</span>}
            {product.is_limited && <span className="tag-limited">LIMITED</span>}
            {discount > 0 && <span className="bg-white text-black text-xs font-bold px-2 py-1">-{discount}%</span>}
          </div>

          {/* Wishlist Button */}
          <button
            onClick={handleWishlistToggle}
            disabled={loading}
            className={`absolute top-3 right-3 p-2 bg-black/50 backdrop-blur-sm transition-all hover:bg-black/80 ${
              wishlisted ? 'text-[#FF3B30]' : 'text-white'
            }`}
            data-testid={`wishlist-toggle-${product.id}`}
          >
            <Heart size={18} fill={wishlisted ? '#FF3B30' : 'none'} />
          </button>

          {/* Quick View Overlay */}
          <div className="quick-view-overlay">
            <span className="btn-primary text-sm py-2 px-4 flex items-center gap-2">
              <ExternalLink size={16} />
              VIEW DETAILS
            </span>
          </div>
        </div>

        {/* Product Info */}
        <div className="p-4">
          <p className="text-white/40 text-xs uppercase tracking-wider mb-1">{product.brand}</p>
          <h3 className="text-white font-medium text-sm mb-2 line-clamp-2 group-hover:text-[#D2FF00] transition-colors">
            {product.name}
          </h3>
          
          {/* Rating */}
          <div className="flex items-center gap-2 mb-3">
            <div className="flex items-center gap-0.5">
              {[...Array(5)].map((_, i) => (
                <Star
                  key={i}
                  size={12}
                  className={i < Math.floor(product.rating) ? 'star-filled fill-current' : 'star-empty'}
                />
              ))}
            </div>
            <span className="text-white/40 text-xs">({product.review_count})</span>
          </div>

          {/* Price */}
          <div className="flex items-center gap-2">
            <span className="price-current text-lg">${product.price.toFixed(2)}</span>
            {discount > 0 && (
              <span className="price-original text-sm">${product.original_price.toFixed(2)}</span>
            )}
          </div>
        </div>
      </Link>
    </motion.div>
  );
};

export default ProductCard;
